<template>
    <v-container fluid v-if="item">
        <v-row class="pl-4 pr-4">
            <v-col cols="12">
                <v-card elevation="0" style="background-color: #F2EDDA;">
                    <v-card-subtitle 
                        :style="getHeadlineStyle(item.priority)"
                        class="text-h6"
                        :alvqa="alvqa + '-headline'"
                    >
                        {{ item.headline }}
                    </v-card-subtitle>
                    
                    <v-card-text>
                        <!-- Image -->
                        <v-img 
                            v-if="item.content && item.content.Image && item.content.Image.filename"
                            :src="item.content.Image.filename"
                            :alt="item.content.Image.alt || item.headline"
                            max-width="100%"
                            max-height="400px"
                            contain
                            class="mb-4 clickable-image"
                            :alvqa="alvqa + '-image'"
                            @click="openImageDialog(item.content.Image)"
                        />
                        
                        <!-- Rich Text Content -->
                        <div 
                            v-if="item.content && item.content.Paragraph"
                            v-html="renderRichText(item.content.Paragraph)"
                            :alvqa="alvqa + '-paragraph'"
                            class="insights-content"
                            @click="handleContentClick"
                        ></div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <!-- Image enlargement dialog -->
        <v-dialog
            v-model="showImageDialog"
            max-width="90vw"
            content-class="image-dialog"
        >
            <v-card>
                <v-card-actions class="justify-end pa-2">
                    <BaseIcon
                        icon="close"
                        @click="showImageDialog = false"
                        color="grey darken-2"
                        :alvqa="alvqa + '-image-dialog-close'"
                    />
                </v-card-actions>
                <v-img
                    v-if="selectedImage && selectedImage.filename"
                    :src="selectedImage.filename"
                    :alt="selectedImage.alt"
                    contain
                    max-height="80vh"
                />
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import { richTextResolver, BlockTypes } from '@storyblok/richtext'
import BaseIcon from '@/components/_buttons/BaseIcon'

export default {
    name: "InsightsReadOnly",
    components: {
        BaseIcon
    },
    props: {
        itemId: {
            type: Number
        }
    },
    data() {
        return {
            alvqa: 'insights-list-read-only',
            showImageDialog: false,
            selectedImage: null
        }
    },
    computed: {
        item() {
            let item = this.$store.getters.getExpandedInsights.find(element => {
                return element.id === this.itemId
            })
            return item
        }
    },
    methods: {
        getHeadlineStyle(priority) {
            const baseStyle = 'word-break: keep-all; hyphens: none;'
            
            if (priority === 'critical') {
                return baseStyle + ' color: #800020 !important;'
            }
            
            return baseStyle
        },

        openImageDialog(image) {
            this.selectedImage = image
            this.showImageDialog = true
        },

        handleContentClick(event) {
            if (event.target.tagName === 'IMG' && event.target.classList.contains('clickable-image')) {
                this.openImageDialog({
                    filename: event.target.src,
                    alt: event.target.alt || 'Image'
                })
            }
        },
        
        renderRichText(richTextObject) {
            if (!richTextObject) return ''
            
            const { render } = richTextResolver({
                resolvers: {
                    'emoji': (node) => {
                        const emoji = node.attrs && node.attrs.emoji ? node.attrs.emoji : ''
                        return emoji
                    },
                    [BlockTypes.IMAGE]: (node) => {
                        const src = node.attrs && node.attrs.src ? node.attrs.src : ''
                        const alt = node.attrs && node.attrs.alt ? node.attrs.alt : 'Image'
                        const title = node.attrs && node.attrs.title ? node.attrs.title : ''
                        return `<img src="${src}" alt="${alt}" title="${title}" class="clickable-image" loading="lazy" />`
                    },
                    [BlockTypes.TABLE]: (node) => {
                        const children = Array.isArray(node.children) ? node.children.join('') : node.children
                        return `<table class="insights-table">${children}</table>`
                    },
                    [BlockTypes.TABLE_ROW]: (node) => {
                        const children = Array.isArray(node.children) ? node.children.join('') : node.children
                        return `<tr>${children}</tr>`
                    },
                    [BlockTypes.TABLE_CELL]: (node) => {
                        let styleAttr = ''
                        let colspanAttr = ''
                        let rowspanAttr = ''
                        
                        if (node.attrs && node.attrs.backgroundColor) {
                            styleAttr = ` style="background-color: ${node.attrs.backgroundColor}"`
                        }
                        if (node.attrs && node.attrs.colspan) {
                            colspanAttr = ` colspan="${node.attrs.colspan}"`
                        }
                        if (node.attrs && node.attrs.rowspan) {
                            rowspanAttr = ` rowspan="${node.attrs.rowspan}"`
                        }
                        
                        const children = Array.isArray(node.children) ? node.children.join('') : node.children
                        return `<td${styleAttr}${colspanAttr}${rowspanAttr}>${children}</td>`
                    },
                    [BlockTypes.TABLE_HEADER]: (node) => {
                        let styleAttr = ''
                        let colspanAttr = ''
                        let rowspanAttr = ''
                        
                        if (node.attrs && node.attrs.backgroundColor) {
                            styleAttr = ` style="background-color: ${node.attrs.backgroundColor}"`
                        }
                        if (node.attrs && node.attrs.colspan) {
                            colspanAttr = ` colspan="${node.attrs.colspan}"`
                        }
                        if (node.attrs && node.attrs.rowspan) {
                            rowspanAttr = ` rowspan="${node.attrs.rowspan}"`
                        }
                        
                        const children = Array.isArray(node.children) ? node.children.join('') : node.children
                        return `<th${styleAttr}${colspanAttr}${rowspanAttr}>${children}</th>`
                    }
                }
            })
            
            try {
                return render(richTextObject)
            } catch (error) {
                console.error('Error rendering rich text:', error)
                return ''
            }
        }
    }
}
</script>

<style scoped>
.insights-content {
    font-size: 15px;
    line-height: 1.5;
}

.insights-content >>> p {
    margin-bottom: 8px;
}

.insights-content >>> ul,
.insights-content >>> ol {
    margin-bottom: 8px;
    padding-left: 20px;
}

.insights-content >>> .insights-table {
    width: 100%;
    border-collapse: collapse;
    margin: 10px 0;
}

.insights-content >>> .insights-table th,
.insights-content >>> .insights-table td {
    border: 1px solid #ddd;
    padding: 8px 12px;
    text-align: left;
    vertical-align: top;
}

.insights-content >>> .insights-table th {
    background-color: #f5f5f5;
    font-weight: 600;
}

.clickable-image {
    cursor: pointer !important;
    transition: transform 0.2s ease, opacity 0.2s ease !important;
}

.clickable-image:hover {
    opacity: 0.9 !important;
    transform: scale(1.01) !important;
}

.insights-content >>> img {
    max-width: 100% !important;
    height: auto !important;
    display: block !important;
    margin: 16px auto !important;
    border-radius: 4px !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
}

.insights-content >>> img.clickable-image {
    cursor: pointer !important;
    transition: transform 0.2s ease, box-shadow 0.2s ease !important;
}

.insights-content >>> img.clickable-image:hover {
    transform: scale(1.02) !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2) !important;
}

.image-dialog .v-card {
    background: white !important;
    box-shadow: none !important;
    border-radius: 8px !important;
    padding: 20px !important;
}

.image-dialog .v-card__actions {
    position: absolute !important;
    top: 0 !important;
    right: 0 !important;
    z-index: 1 !important;
}
</style>
